import { useLayoutEffect, useSyncExternalStore } from 'react';

import { useInject } from '@vk-hr-tek/core/ioc';
import { Box, SpacingValue } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';

import { useIsWidget } from '@app/hooks';

import { useIsServiceDegraded } from '../hooks';

import { DegradationBannerImage } from './DegradationBannerImage';
import { DegradationBannerSlotService } from './DegradationBannerSlot.service';
import { useStyles } from './DegradationBanner.styles';

interface DegradationBannerProps {
  /**
   * Добавить отступы слева и справа, чтобы выровнять баннер по контенту страницы
   */
  withHorizontalOffset?: boolean;
  /**
   * Баннер отрисован внутри страницы: баннер layout при этом скрывается.
   * В виджете встроенный баннер не показывается
   */
  embedded?: boolean;
  /**
   * Отступ сверху
   */
  mt?: SpacingValue;
}

/**
 * Баннер о деградации сервиса. Показывается, если хотя бы один HTTP-ответ содержал признак деградации
 *
 * @param withHorizontalOffset - Добавить отступы слева и справа, чтобы выровнять баннер по контенту страницы
 * @param embedded - Баннер отрисован внутри страницы: баннер layout при этом скрывается
 * @param mt - Отступ сверху
 */
export const DegradationBanner = ({
  withHorizontalOffset,
  embedded,
  mt,
}: DegradationBannerProps) => {
  const isDegraded = useIsServiceDegraded();
  const isWidget = useIsWidget();
  const slot = useInject(DegradationBannerSlotService);
  const isEmbedded = useSyncExternalStore(slot.subscribe, slot.isEmbedded);
  const { classes } = useStyles();

  useLayoutEffect(() => {
    if (embedded) {
      return slot.register();
    }
  }, [embedded, slot]);

  if (!isDegraded || (embedded ? isWidget : isEmbedded)) {
    return null;
  }

  return (
    <Box
      display="flex"
      mt={mt}
      mb="16"
      mx={withHorizontalOffset ? '24' : undefined}
      border={1}
      borderColor="stroke.primary"
      radius="l"
      overflow="hidden"
      className={classes.root}
      testId="degradation-banner"
    >
      <Box
        display="flex"
        flexDirection="column"
        flex="1 1 0"
        gap="8"
        px="16"
        py="20"
      >
        <Typography variant="subtitle1">
          Часть функций может быть недоступна
        </Typography>
        <Typography variant="body3">Это временно — уже чиним</Typography>
      </Box>
      <Box className={classes.imageArea}>
        <DegradationBannerImage />
      </Box>
    </Box>
  );
};
